import {AfterViewInit, Directive, ElementRef, Renderer2} from '@angular/core';

/**
 * Angular Material sets role="tablist" on the <mat-stepper> host, but its own
 * template also nests role="tabpanel" content panels as direct descendants of
 * that same host — an invalid ARIA structure (a tablist may only own tab
 * children). This moves the tablist role onto the header container, which
 * only ever contains the tab buttons. A tablist also requires an accessible
 * name (axe reports "Accessible Text: empty" otherwise), so we give it one —
 * preferring a preceding page heading when one is available.
 */
@Directive({
    selector: 'mat-stepper, mat-horizontal-stepper, mat-vertical-stepper, [matStepper]'
})
export class StepperAriaFixDirective implements AfterViewInit {
    constructor(private el: ElementRef<HTMLElement>, private renderer: Renderer2) {
    }

    ngAfterViewInit(): void {
        const host = this.el.nativeElement;
        const headerContainer = host.querySelector('.mat-horizontal-stepper-header-container');
        if (headerContainer) {
            this.renderer.removeAttribute(host, 'role');
            this.renderer.setAttribute(headerContainer, 'role', 'tablist');

            const heading = this.findPrecedingHeading(host);
            if (heading) {
                if (!heading.id) {
                    this.renderer.setAttribute(heading, 'id', 'stepper-heading-' + Math.random().toString(36).slice(2, 9));
                }
                this.renderer.setAttribute(headerContainer, 'aria-labelledby', heading.id);
            } else {
                this.renderer.setAttribute(headerContainer, 'aria-label', this.buildStepListLabel(host));
            }
        }
    }

    // Names the tablist after its own steps (e.g. "Application steps: Additional Information, Payment, Review")
    // instead of a generic placeholder, since Material's step labels are already the most accurate description available.
    private buildStepListLabel(host: HTMLElement): string {
        const stepLabels = Array.from(host.querySelectorAll('.mat-step-text-label'))
            .map(el => el.textContent?.trim())
            .filter((text): text is string => !!text);
        return stepLabels.length ? `Application steps: ${stepLabels.join(', ')}` : 'Application steps';
    }

    private findPrecedingHeading(host: HTMLElement): HTMLElement | null {
        let sibling = host.previousElementSibling;
        while (sibling) {
            if (/^H[1-6]$/.test(sibling.tagName)) {
                return sibling as HTMLElement;
            }
            const nested = sibling.querySelector('h1, h2, h3, h4, h5, h6');
            if (nested) {
                return nested as HTMLElement;
            }
            sibling = sibling.previousElementSibling;
        }
        return null;
    }
}
